import Const from '../actions/ActionConst';
import {
  OpenDiscoveryInternalReducer,
  OpenDiscoveryReducer,
} from './OpenDiscoveryReducer';
import _, {
  filter,
  find,
  flatten,
  get,
  includes,
  isUndefined,
  isEmpty,
  map,
} from 'lodash';
import { composeResetReducer } from 'redux-reset-store';
import { Viz } from '../../../discovery/VizUtil';
import { ChartSpecIds, VIZ_OPTION_IDS } from '../../Constants';
import InListFilter from '../../../discovery/filter/exports/InListFilter';
import { ChartSpecs } from '../../../discovery/ChartSpecs';
import store from '../index';
import {
  IDiscovery,
  IDiscoveryTracked,
  IDiscoveryType,
  IViz,
  StringBool,
} from '../../../discovery';
import {
  ILegendDatum,
  ILegendGroupDatum,
} from '../../../discovery/charts/viz-legend';
import { IDrillContext } from '../../../discovery/viz-redirect';
import {
  IExpressionSegment,
  ITimeCalcAttribute,
} from '../../../datasets/interfaces';
import { shortid } from '../../utilities/shortid-adapter';
import { updateFocusedGroupData } from '../../utilities/update-focused-group-data';

interface IMonitor {
  [key: string]: any;
  payload: any;
  discoveryType: IDiscoveryType;
  description: string;
}

export interface IDiscoverReducerState {
  monitors: IMonitor[];
  fetching: boolean;
  fetched: boolean;
  error: any;
  deleting: boolean;
  deleted: boolean;
  showAddMonitorDialog: boolean;
  showSaveDialog: boolean;
  showSaveAsDialog?: boolean;
  saveActive: boolean;
  openDiscoveries: {
    [key: string]: IDiscoveryTracked;
  };
  openDiscoveriesTabList: string[];
  displayDiscovery: string;
  prevDisplayDiscovery: null; // doesn't seem to change from null
  queryResults: any;
  rangeSelection: '30d';
  pinnedDiscoveriesLoading: boolean;
  toBeDeleted?: any;
  toBeAdded?: any;
  drillContext?: IDrillContext;
  drillContextUpdating?: boolean;
  linkStrategy?: string;
}

export const initialState: IDiscoverReducerState = {
  monitors: [],
  fetching: false,
  fetched: false,
  error: null,
  deleting: false,
  deleted: false,
  showAddMonitorDialog: false,
  showSaveDialog: false,
  saveActive: false,
  openDiscoveries: {},
  openDiscoveriesTabList: [],
  displayDiscovery: null,
  prevDisplayDiscovery: null,
  queryResults: null,
  rangeSelection: '30d',
  pinnedDiscoveriesLoading: false,
};

const DiscoveryReducers = {};

/**
 * Replaces the open viz outside the undo history. redux-undo moves `_latestUnfiltered` into the
 * past on the next undoable action, so it carries the new viz too, or undo would restore the old one.
 */
const replaceOpenViz = (open: IDiscoveryTracked, viz: IViz) => {
  open.present = { ...open.present, viz };
  const latest = (open as any)._latestUnfiltered;
  if (!isUndefined(latest)) {
    (open as any)._latestUnfiltered = { ...latest, viz };
  }
};

export default composeResetReducer(
  (state = initialState, action) => {
    const currentOpenDiscoveries = state.openDiscoveries ?? {};
    const currentOpenDiscovery = currentOpenDiscoveries[action.discoveryId]
      ? currentOpenDiscoveries[action.discoveryId].present
      : null;

    const enforcePresentState = () => {
      if (_.isNil(currentOpenDiscovery)) {
        throw new Error(
          `Failed to process ${action.type}. state.openDiscoveries[${action.discoveryId}].present is not known.`,
        );
      }
    };

    try {
      switch (action.type) {
        case Const.Discover.SET_SHOW_ADD_MONITOR_DIALOG:
          return {
            ...state,
            showAddMonitorDialog: action.show,
          };
        case Const.Discover.SET_SHOW_ADD_COMP_MONITOR:
          return {
            ...state,
            showAddComparisonPeriodMonitorDialog: action.show,
          };
        case Const.Discover.POST_MONITORS_FULFILLED:
          return {
            ...state,
            showAddMonitorDialog: false,
          };
        case Const.Discover.CLOSE_OPEN_DISCOVERY:
          // Find removed discoveries and de-register reducer

          _.forEach(
            _.difference(
              _.keys(state.openDiscoveries),
              _.keys(action.openDiscoveries),
            ),
            (discoveryId: any) => delete DiscoveryReducers[discoveryId],
          );

          return {
            ...state,
            openDiscoveries: action.openDiscoveries,
            openDiscoveriesTabList: action.openDiscoveriesTabList,
            prevDisplayDiscovery: action.prevDisplayDiscovery,
            displayDiscovery: action.displayDiscovery,
          };
        case Const.Discover.CLOSE_OPEN_DELETED_DISCOVERY:
          // de-register reducer
          delete DiscoveryReducers[action.discoveryId];

          return {
            ...state,
            openDiscoveries: action.openDiscoveries,
            openDiscoveriesTabList: action.openDiscoveriesTabList,
            prevDisplayDiscovery: action.prevDisplayDiscovery,
            displayDiscovery: action.displayDiscovery,
          };
        case Const.Discover.ADD_OPEN_DISCOVERY: {
          const newDiscoveryMap = {
            ...state.openDiscoveries,
            [action.discoveryId]: {
              options: {
                [VIZ_OPTION_IDS.configPanelDetail]: 'layout',
                [VIZ_OPTION_IDS.panelDetail]: null,
                [VIZ_OPTION_IDS.showPanel]: true,
              },
              present: {
                isPrivate: true,
              },
            },
          };
          DiscoveryReducers[action.discoveryId] = OpenDiscoveryReducer(
            action.discoveryId,
          );

          let openDiscoveriesTabList = [
            ...state.openDiscoveriesTabList,
            action.discoveryId,
          ];

          if (_.isInteger(action.tabIndex) && action.tabIndex > -1) {
            // if maintaining an order
            openDiscoveriesTabList = [
              ..._.slice(state.openDiscoveriesTabList, 0, action.tabIndex),
              action.discoveryId,
              ..._.slice(state.openDiscoveriesTabList, action.tabIndex),
            ];
          }

          return {
            ...state,
            openDiscoveries: newDiscoveryMap,
            openDiscoveriesTabList: _.uniq(openDiscoveriesTabList),
          };
        }
        case Const.Discover.SET_PINNED_DISCOVERIES_LOADING: {
          return {
            ...state,
            pinnedDiscoveriesLoading: action.loading,
          };
        }
        case Const.Discover.SET_VIZ_IS_EMPTY:
          return {
            ...state,
            vizIsEmpty: action.isEmpty,
          };
        case Const.Discover.SET_DISPLAY_DISCOVERY:
          return {
            ...state,
            displayDiscovery: action.displayDiscovery,
            prevDisplayDiscovery: action.prevDisplayDiscovery,
            discoveryAlreadyRendered: action.discoveryAlreadyRendered,
          };
        case Const.Discover.POST_QUERY_FULFILLED:
          return {
            ...state,
            queryResults: action.queryResults,
          };
        case Const.Discover.SET_SHOW_NEW_VIZ_DIALOG:
          return {
            ...state,
            showNewVizDialog: action.show,
          };
        case Const.Discover.SET_SHOW_SAVE_DIALOG:
          return {
            ...state,
            showSaveDialog: action.show,
            closeDialogAfterSave: action.closeDialogAfterSave ?? false,
          };
        case Const.Discover.SET_SHOW_VERSION_DIALOG:
          return {
            ...state,
            showVersionDialog: action.show,
          };
        case Const.Discover.SET_SHOW_SAVE_AS_DIALOG:
          return {
            ...state,
            showSaveAsDialog: action.show,
          };
        case Const.Discover.SET_SAVE_ACTIVE:
          return {
            ...state,
            saveActive: action.value,
          };
        case Const.Discover.SET_SAVE_ERROR:
          return {
            ...state,
            saveError: action.msg,
          };
        case Const.Discover.SET_VIZ_TRASH_DROP_TARGET: {
          return {
            ...state,
            dndTrashTargetId: action.dndTrashTargetId,
          };
        }
        case Const.Discover.BEGIN_NEW_VIZ_OPENING:
          return {
            ...state,
            newVizOpening: true,
          };
        case Const.Discover.SET_NEW_VIZ:
          return {
            ...state,
            newViz: action.newViz,
            newVizOpening: false,
          };
        case Const.Discover.BEGIN_REPORT_DETAIL_UPDATE:
          return {
            ...state,
            drillContextUpdating: true,
          };
        case Const.Discover.SET_REPORT_DETAIL_INFO:
          return {
            ...state,
            drillContext: action.drillContext,
            linkStrategy: action.linkStrategy,
            drillContextUpdating: false,
          };
        case Const.Discover.SET_VIZ_ID: {
          enforcePresentState();
          let { openDiscoveries } = state;
          const dis = openDiscoveries[action.discoveryId].present;

          // Set ID in the viz member
          const viz = { ...dis.viz, id: action.id };
          if (!_.isNil(action.creatorName)) {
            viz.creatorName = action.creatorName;
          }
          if (!_.isNil(action.creator)) {
            viz.creator = action.creator;
          }

          // swap open discovery to new ID
          openDiscoveries[action.discoveryId].present = {
            ...dis,
            viz,
            id: action.id,
          };
          openDiscoveries[action.id] = openDiscoveries[action.discoveryId];

          const replaceState = (discState: IDiscovery) => {
            discState.id = action.id;
            discState.viz.id = action.id;
            discState.viz.name = openDiscoveries[action.id].present?.viz?.name;
          };

          _.forEach(openDiscoveries[action.id].past, replaceState);
          _.forEach(openDiscoveries[action.id].future, replaceState);

          if (
            !isUndefined((openDiscoveries[action.id] as any)._latestUnfiltered)
          ) {
            // https://redux-undo.js.org/main/faq#what-is-_latestunfiltered-can-i-remove-it
            (
              (openDiscoveries[action.id] as any)
                ._latestUnfiltered as IDiscovery
            ).id = action.id;
            (
              (openDiscoveries[action.id] as any)
                ._latestUnfiltered as IDiscovery
            ).viz.id = action.id;
            (
              (openDiscoveries[action.id] as any)
                ._latestUnfiltered as IDiscovery
            ).viz.name = openDiscoveries[action.id].present?.viz?.name;
          }

          DiscoveryReducers[action.id] = OpenDiscoveryReducer(action.id);

          // Replace old entry in tab list with new id, preserves position
          const tabList = state.openDiscoveriesTabList;
          tabList.splice(_.indexOf(tabList, action.discoveryId), 1, action.id);

          // Remove the old entry, creates a new object to poke state subscribers
          openDiscoveries = _.omit(openDiscoveries, action.discoveryId);

          // Update Display Discovery if was pointing to previous, otherwise preserve
          const displayDiscovery =
            state.displayDiscovery === action.discoveryId
              ? action.id
              : state.displayDiscovery;
          return {
            ...state,
            openDiscoveries,
            openDiscoveriesTabList: tabList,
            displayDiscovery,
          };
        }
        case Const.Discover.SET_DISCOVERY_DIRTY: {
          enforcePresentState();
          const discoveries = state.openDiscoveries;
          const discovery = discoveries[action.discoveryId].present;
          discoveries[action.discoveryId].present = {
            ...discovery,
            dirty: action.dirty,
          };
          return {
            ...state,
            openDiscoveries: discoveries,
          };
        }
        case Const.Discover.SET_SAVE_CHECKPOINT: {
          const { discoveryId, updatedOn, revisions } = action;
          const discoveries = { ...state.openDiscoveries };

          if (!discoveries[discoveryId]) {
            return state;
          }

          const discovery: IDiscoveryTracked = {
            ...discoveries[action.discoveryId],
          };
          discovery.saveCheckpoint = (discovery?.past ?? []).length;
          discovery.present.viz.updatedOn = updatedOn;
          discovery.present.viz.revisions = revisions;
          discoveries[discoveryId] = _.omit(
            discovery,
            'present.revisionSelected',
          );

          return {
            ...state,
            openDiscoveries: discoveries,
          };
        }
        case Const.Discover.SET_VIZ_LEGEND_DATA: {
          enforcePresentState();
          const openViz = state.openDiscoveries;
          const disc = openViz[action.discoveryId].present;
          disc.legendData = [];
          const ld: ILegendDatum[] = [...action.legendData];
          const legendGroupData: ILegendGroupDatum[] = Viz.getOptionFromViz({
            viz: disc.viz,
            option: VIZ_OPTION_IDS.legendGroupMetadata,
            defaultValue: [],
          });
          const groupedFields = flatten(map(legendGroupData, 'fields'));
          const legendData = filter(
            ld,
            item => !includes(groupedFields, item.label),
          );
          openViz[action.discoveryId].present = {
            ...disc,
            legendData,
            initialLegendData: ld,
          } as IDiscovery;
          return {
            ...state,
            openDiscoveries: openViz,
          };
        }
        case Const.Discover.SET_SHOW_VIZ_FILTER_DIALOG: {
          let { field } = action;
          if (_.isString(field)) {
            const vizID = state.displayDiscovery;
            const { viz, goodFields = [] } =
              state.openDiscoveries[vizID].present;
            const calcFields = Viz.getCalcsFromViz(viz);
            field =
              _.find([...goodFields, ...calcFields], {
                name: field,
              }) || field;
          }
          return {
            ...state,
            activeFilterField: field,
            showFieldFilterDialog: action.show,
          };
        }
        case Const.Discover.SET_ACTIVE_VIZ_FIELD_FILTER: {
          return {
            ...state,
            activeFilter: { ...action.filter },
          };
        }
        case Const.Discover.CLEAR_VIZ_SORTING: {
          const open = state.openDiscoveries;
          const disc = open[action.discoveryId].present;
          const viz = { ...disc.viz };
          const options = { ...viz.options };
          let querySort;
          try {
            querySort = JSON.parse(options[VIZ_OPTION_IDS.querySort]);
          } catch {
            querySort = {};
          }
          if (action.field) {
            delete querySort[action.field.name];
          }
          options[VIZ_OPTION_IDS.querySort] = JSON.stringify(querySort);
          viz.options = options;
          open[action.discoveryId].present.viz = viz;
          return {
            ...state,
            openDiscoveries: open,
          };
        }
        case Const.Discover.SET_SHOW_VIZ_CALC_DIALOG: {
          return {
            ...state,
            showFieldCalcDialog: action.show,
            activeCalcField: action.field,
          };
        }
        case Const.Discover.SET_TOOLTIP_DATA: {
          enforcePresentState();
          const open = state.openDiscoveries;
          const disc = open[action.discoveryId].present;
          if (
            _.isEqual(disc.tooltipData, action.tooltipData) ||
            (disc.tooltipData &&
              action.tooltipData &&
              _.isEqual(disc.tooltipData.hoverX, action.tooltipData.hoverX) &&
              _.isEqual(disc.tooltipData.hoverY, action.tooltipData.hoverY))
          ) {
            // Equivalent to current state, return without modification
            return state;
          }
          open[action.discoveryId].present = {
            ...disc,
            tooltipData: action.tooltipData,
          };
          return {
            ...state,
            openDiscoveries: open,
          };
        }
        case Const.Discover.SET_HOVER_OVER_VIZ_DATA: {
          enforcePresentState();
          const open = state.openDiscoveries;
          const disc = open[action.discoveryId].present;
          open[action.discoveryId].present = {
            ...disc,
            hoverData: action.data,
            hoverEvent: action.event,
          };
          return {
            ...state,
            openDiscoveries: open,
          };
        }
        case Const.Discover.AUTO_HYDRATE_VIZ_FIELD_FILTER: {
          const open = state.openDiscoveries;
          const disc = open[state.displayDiscovery].present;
          const { viz } = disc;
          const filters = Viz.getFiltersFromViz(viz);
          const filterTarget =
            filters[action.filterField.name] ||
            InListFilter(action.filterField, action.filterList);
          (filterTarget.expression.left as IExpressionSegment).operands =
            action.filterList;
          _.set(filterTarget, 'info.selected', action.filterList.length);
          filters[action.filterField.name] = filterTarget;
          const stringFilters = JSON.stringify(filters);
          viz.options.filters = stringFilters;
          return {
            ...state,
            openDiscoveries: open,
          };
        }
        case Const.Discover.CLEAR_VIZ_QUERY: {
          enforcePresentState();
          const open = state.openDiscoveries;
          const disc = open[action.discoveryId].present;
          open[action.discoveryId].present = {
            ...disc,
            vizQueryResults: null,
          };
          return {
            ...state,
            openDiscoveries: open,
          };
        }
        case Const.Discover.VIZ_QUERY_LOADING: {
          enforcePresentState();
          const open = state.openDiscoveries;
          const disc = open[action.discoveryId].present;
          const openDiscovery = open[action.discoveryId];

          return {
            ...state,
            openDiscoveries: {
              ...open,
              [action.discoveryId]: {
                ...openDiscovery,
                present: {
                  ...disc,
                  vizLoading: action.loading,
                  vizQueryResults: null,
                  vizQueryError: null,
                },
              },
            },
          };
        }
        case Const.Discover.VIZ_DEFINITION_LOADED: {
          enforcePresentState();
          const open = state.openDiscoveries;
          const disc = open[action.discoveryId].present;
          const openDiscovery = open[action.discoveryId];

          return {
            ...state,
            openDiscoveries: {
              ...open,
              [action.discoveryId]: {
                ...openDiscovery,
                present: {
                  ...disc,
                  vizLoading: action.loading,
                },
              },
            },
          };
        }
        case Const.Discover.VIZ_QUERY_FINISHED: {
          enforcePresentState();
          const open = state.openDiscoveries;
          const disc = open[action.discoveryId].present;
          const openDiscovery = open[action.discoveryId];

          return {
            ...state,
            openDiscoveries: {
              ...open,
              [action.discoveryId]: {
                ...openDiscovery,
                present: {
                  ...disc,
                  vizLoading: action.loading,
                  vizQueryResults: action.data,
                  vizQuerySecondaryResults: action.secondaryData,
                  vizQueryError: null,
                  vizQueryWarning: null,
                  focusedData: [],
                  focusedDataPoints: [],
                  collectDetailInfoArgs: null,
                },
              },
            },
          };
        }
        case Const.Discover.VIZ_QUERY_WARNING: {
          enforcePresentState();
          const open = state.openDiscoveries;
          const disc = open[action.discoveryId].present;
          if (!_.isEmpty(open[action.discoveryId].present?.vizQueryWarning)) {
            // discarding out-of-date query result
            return state;
          }
          open[action.discoveryId].present = {
            ...disc,
            vizLoading: action.loading,
            vizQueryResults: null,
            vizQueryWarning: {
              warnings: action.warnings,
              queryVariables: action.queryVariables,
            },
            vizQueryError: null,
          };
          return {
            ...state,
            openDiscoveries: open,
          };
        }
        case Const.Discover.VIZ_QUERY_ERROR: {
          enforcePresentState();
          const open = state.openDiscoveries;
          const disc = open[action.discoveryId].present;

          open[action.discoveryId].present = {
            ...disc,
            vizLoading: action.loading,
            vizQueryResults: null,
            vizQueryError: {
              error: action.error,
              queryOptions: action.queryOptions,
            },
            vizQueryWarning: null,
          };
          return {
            ...state,
            openDiscoveries: open,
          };
        }
        case Const.Discover.IGNORE_VIZ_QUERY_ERROR: {
          enforcePresentState();
          const open = state.openDiscoveries;
          const disc = open[action.discoveryId].present;

          open[action.discoveryId].present = {
            ...disc,
            vizQueryError: undefined,
          };
          return {
            ...state,
            openDiscoveries: open,
          };
        }
        case Const.Discover.SHOW_CONFIRM_REMOVE_FIELD: {
          if (action.show) {
            return {
              ...state,
              showConfirmRemoveField: action.show,
              toBeDeleted: {
                field: action.field,
                discoveryId: action.discoveryId,
                shelf: action.shelf,
              },
            };
          } else {
            const newState = { ...state };
            if (newState.toBeDeleted) {
              delete newState.toBeDeleted;
            }
            return {
              ...newState,
              showConfirmRemoveField: action.show,
            };
          }
        }
        case Const.Discover.SHOW_CONFIRM_ADD_FIELD: {
          if (action.show) {
            return {
              ...state,
              showConfirmAddField: action.show,
              toBeAdded: {
                field: action.field,
                discoveryId: action.discoveryId,
                shelf: action.shelf,
                breakingFields: action.breakingFields,
                insertionPosition: action.insertionPosition,
              },
            };
          } else {
            const newState = { ...state };
            if (newState.toBeAdded) {
              delete newState.toBeAdded;
            }
            return {
              ...newState,
              showConfirmAddField: action.show,
            };
          }
        }

        case Const.Discover.SET_LIVE_QUERY: {
          enforcePresentState();
          const { openDiscoveries } = state;
          const val: StringBool = _.isString(action.value)
            ? action.value
            : action.value
              ? 'true'
              : 'false';
          const dis = openDiscoveries[action.discoveryId].present;
          const options = {
            ...dis.viz.options,
            [VIZ_OPTION_IDS.useLiveQuery]: val,
          };
          const viz = {
            ...dis.viz,
            options,
          };
          openDiscoveries[action.discoveryId].present = {
            ...dis,
            viz,
          };
          return {
            ...state,
            openDiscoveries: { ...openDiscoveries },
          };
        }

        case Const.Discover.UPDATE_VIZ_NAME: {
          enforcePresentState();
          const { openDiscoveries } = state;
          const dis = openDiscoveries[action.discoveryId].present;
          // Set ID in the viz member
          const viz = { ...dis.viz, name: action.newName };
          openDiscoveries[action.discoveryId].present = {
            ...dis,
            viz,
          };

          return {
            ...state,
            openDiscoveries: { ...openDiscoveries },
          };
        }

        case Const.Discover.COPY_VIZ_STATE: {
          const { openDiscoveries } = state;
          const from = openDiscoveries[action.fromId];
          const to = openDiscoveries[action.toId];
          Object.assign(
            to ?? {},
            _.omit(from, ['past', 'future', 'present', 'history']),
          );
          return {
            ...state,
            openDiscoveries: { ...openDiscoveries },
          };
        }

        case Const.Discover.COPY_UNDO_STACK: {
          const { openDiscoveries } = state;
          const from = openDiscoveries[action.fromId];
          const to = openDiscoveries[action.toId];
          const toPast = from.past.map(past => {
            const t = {
              ...past,
              id: action.toId,
              viz: { ...past.viz, id: action.toId, name: action.toName },
            };
            return t;
          });
          const toFuture = from.future.map(future => {
            const t = {
              ...future,
              id: action.toId,
              viz: { ...future.viz, id: action.toId, name: action.toName },
            };
            return t;
          });
          to.past = [...toPast];
          to.future = [...toFuture];
          to.history.past = [...toPast];
          to.history.future = [...toFuture];
          to.present = { ...to.present, id: action.toId };

          return {
            ...state,
            openDiscoveries: { ...openDiscoveries },
          };
        }
        case Const.Discover.UPDATE_DATASET_FOR_DISCOVERY: {
          const { openDiscoveries } = state;
          const open = openDiscoveries[action.discoveryId];
          if (!_.isNil(open?.present?.viz)) {
            const { viz } = open.present;

            // replace layout attributes
            const layout = _.mapValues(viz.layout, shelf =>
              shelf
                .map(attr => {
                  let newAttr;
                  const datasetAttr = action.dataset.attributes.find(a => {
                    return a.name === attr.name;
                  });
                  if (!_.isNil(datasetAttr)) {
                    newAttr = datasetAttr;
                  } else if (
                    _.isNil(datasetAttr) &&
                    !_.isEmpty(attr.parentField)
                  ) {
                    // this is a hierarchy calc, make sure it is preserved in the layout
                    const parentAttr = action.dataset.attributes.find(
                      a => a.name === attr.parentField.name,
                    );
                    if (!_.isNil(parentAttr)) {
                      newAttr = { ...attr, parentField: { ...parentAttr } };
                    } else {
                      // the parent field has disappeared, the time calc isn't valid anymore
                    }
                  } else if (!_.isEmpty((attr as ITimeCalcAttribute).formula)) {
                    // viz calc. it needs to remain
                    newAttr = { ...attr };
                  }
                  return newAttr;
                })
                .filter(f => !_.isNil(f)),
            );

            replaceOpenViz(open, {
              ...viz,
              dataset: { ...action.dataset },
              layout,
            });
          }
          return {
            ...state,
            openDiscoveries: { ...openDiscoveries },
          };
        }
        case Const.Discover.REMOVE_MISSING_FIELDS: {
          const { openDiscoveries } = state;
          const open = openDiscoveries[action.discoveryId];
          if (!_.isNil(open)) {
            const viz = { ...open.present.viz };
            const layout = { ...viz.layout };

            action.fieldsToRemove.forEach(remove => {
              const shelf = Viz.findShelfContainingField(layout, remove.name);
              if (!_.isNil(shelf)) {
                const shelfFields = layout[shelf];
                const removed = shelfFields.filter(f => {
                  return f.name !== remove.name;
                });
                layout[shelf] = [...removed];
              }
            });

            // is it a saved calc or time-calc?
            const calcs = Viz.getCalcsFromViz(viz);
            if (!_.isEmpty(calcs)) {
              const updatedCalcs = calcs.filter(c => {
                return !_.some(action.fieldsToRemove, _.pick(c, 'name'));
              });
              viz.options[VIZ_OPTION_IDS.calcFields] =
                JSON.stringify(updatedCalcs);
            }

            const timeCalcs = Viz.getTimeCalcsFromViz(viz);
            if (!_.isEmpty(timeCalcs)) {
              const finalTimeCalcs = Object.entries(timeCalcs).reduce(
                (accum, [key, c]) => {
                  // remove invalid fields
                  const updated = c.filter(tc => {
                    return !_.some(action.fieldsToRemove, _.pick(tc, 'name'));
                  });
                  if (!_.isEmpty(updated)) {
                    accum[key] = updated;
                  }
                  return accum;
                },
                {},
              );
              viz.options[VIZ_OPTION_IDS.timeHierarchies] =
                JSON.stringify(finalTimeCalcs);
            }
            const filters = Viz.getFiltersFromViz(viz);
            const goodFilters = Object.values(filters).reduce((accum, f) => {
              if (!_.some(action.filtersToRemove, _.pick(f, 'field'))) {
                accum[f.field] = f;
              }
              return accum;
            }, {});
            const updatedOptions = {
              ...viz.options,
              filters: JSON.stringify(goodFilters),
            };

            delete open.present.missingFields;
            delete open.present.missingDependentFields;
            delete open.present.goodFields;
            delete open.present.missingFilters;

            openDiscoveries[action.discoveryId].present.viz = {
              ...viz,
              layout: { ...layout },
              options: { ...updatedOptions },
            };
          }
          return {
            ...state,
            openDiscoveries: { ...openDiscoveries },
          };
        }
        case Const.Discover.REMOVE_HIDDEN_FIELDS: {
          const { openDiscoveries } = state;
          const open = openDiscoveries[action.discoveryId];
          if (!_.isNil(open?.present?.viz)) {
            const { viz } = open.present;
            const {
              fieldNames,
              filterNames,
            }: { fieldNames: string[]; filterNames: string[] } = action;
            const isRemoved = ({ name }) => includes(fieldNames, name);

            const layout = { ...viz.layout };
            const removedSlicerNames: string[] = [];
            fieldNames.forEach(name => {
              Viz.findShelvesContainingField(layout, name).forEach(shelf => {
                layout[shelf] = _.reject(layout[shelf], { name });
                if (shelf === ChartSpecIds.SLICER) {
                  removedSlicerNames.push(name);
                }
              });
            });

            const options = {
              ...viz.options,
              [VIZ_OPTION_IDS.filters]: JSON.stringify(
                _.omitBy(Viz.getFiltersFromViz(viz), filter =>
                  includes(filterNames, filter?.field),
                ),
              ),
            };
            // A selection left behind still becomes a query filter on the unavailable column.
            if (
              !_.isEmpty(removedSlicerNames) &&
              _.has(viz.options, VIZ_OPTION_IDS.slicerSelections)
            ) {
              options[VIZ_OPTION_IDS.slicerSelections] = JSON.stringify(
                _.reject(Viz.getSlicerSelections(viz), ({ name }) =>
                  includes(removedSlicerNames, name),
                ),
              );
            }
            const calcs = Viz.getCalcsFromViz(viz);
            if (!_.isEmpty(calcs)) {
              options[VIZ_OPTION_IDS.calcFields] = JSON.stringify(
                _.reject(calcs, isRemoved),
              );
            }
            const timeCalcs = Viz.getTimeCalcsFromViz(viz);
            if (!_.isEmpty(timeCalcs)) {
              options[VIZ_OPTION_IDS.timeHierarchies] = JSON.stringify(
                _.omitBy(
                  _.mapValues(timeCalcs, calcsOfField =>
                    _.reject(calcsOfField, isRemoved),
                  ),
                  _.isEmpty,
                ),
              );
            }

            replaceOpenViz(open, { ...viz, layout, options });
          }
          return {
            ...state,
            openDiscoveries: { ...openDiscoveries },
          };
        }

        case Const.Discover.SET_FOCUSED_VIZ_DATA: {
          enforcePresentState();
          const openDiscoveries = _.cloneDeep(state.openDiscoveries);
          const activeDiscovery = openDiscoveries[action.discoveryId].present;
          let { focusedData = [], focusedDataPoints = [] } = activeDiscovery;
          const legendGroupData: ILegendGroupDatum[] = Viz.getOptionFromViz({
            viz: activeDiscovery.viz,
            option: VIZ_OPTION_IDS.legendGroupMetadata,
            defaultValue: [],
          });
          let newFocusedData = [action.data];
          const focusedDataFromGroup = get(
            find(legendGroupData, ({ fields }) =>
              fields.includes(action.data?.label),
            ),
            'fields',
            [],
          );
          if (!isEmpty(focusedDataFromGroup)) {
            newFocusedData = map(focusedDataFromGroup, label => ({ label }));
          }

          const index = _.findIndex(focusedData, action.data);
          if (_.isNil(action.data)) {
            focusedData = [];
          } else if (action.multiSelectEnabled) {
            if (index === -1) {
              newFocusedData.forEach(item => {
                focusedData.push(item);
              });
            } else {
              focusedData.splice(index, 1);
            }
          } else {
            focusedData =
              index === -1 || focusedData.length > 1 ? newFocusedData : [];
          }

          if (!_.isEmpty(focusedData)) {
            // remove data points not in focusedData
            focusedDataPoints = _.filter(
              focusedDataPoints,
              _focusedDataPoint => {
                return _.some(focusedData, _pathInfo =>
                  _.isEqual(_focusedDataPoint.pathInfo, _pathInfo),
                );
              },
            );
          }

          openDiscoveries[action.discoveryId].present = {
            ...activeDiscovery,
            focusedData,
            focusedDataPoints,
            hoverEvent: action.event,
          };

          return {
            ...state,
            openDiscoveries,
          };
        }
        case Const.Discover.SET_FOCUSED_GROUP_VIZ_DATA: {
          enforcePresentState();
          const openDiscoveries = _.cloneDeep(state.openDiscoveries);
          const activeDiscovery = openDiscoveries[action.discoveryId].present;
          const { focusedData, focusedDataPoints } = updateFocusedGroupData(
            activeDiscovery,
            action,
          );

          openDiscoveries[action.discoveryId].present = {
            ...activeDiscovery,
            focusedData,
            focusedDataPoints,
            hoverEvent: action.event,
          };

          return {
            ...state,
            openDiscoveries,
          };
        }

        case Const.Discover.TOGGLE_FOCUSED_VIZ_DATA_POINTS: {
          enforcePresentState();
          const openDiscoveries = _.cloneDeep(state.openDiscoveries);
          const activeDiscovery = openDiscoveries[action.discoveryId].present;

          const { pointData } = action;
          const { focusedDataPoints = [] } = activeDiscovery;

          // remove point if it exists in array and remove all that are not on same x value
          const pointDataSaved = _.pick(pointData, [
            'posX',
            'posY',
            'xAxis',
            'pathInfo',
          ]);
          const updatedFocusedDataPoints = _.reject(
            focusedDataPoints,
            _point => {
              return (
                _.matches(pointDataSaved)(_point) ||
                !_.isEqual(_point.posX, pointDataSaved.posX)
              );
            },
          );

          // add point if did not exist in array
          if (_.size(updatedFocusedDataPoints) === _.size(focusedDataPoints)) {
            updatedFocusedDataPoints.push(pointDataSaved);
          }

          openDiscoveries[action.discoveryId].present = {
            ...activeDiscovery,
            collectDetailInfoArgs: _.isEmpty(updatedFocusedDataPoints)
              ? null
              : action.collectDetailInfoArgs,
            focusedDataPoints: updatedFocusedDataPoints,
          };

          return {
            ...state,
            openDiscoveries,
          };
        }

        case Const.Discover.SET_FOCUSED_VIZ_DATA_POINTS: {
          enforcePresentState();
          const openDiscoveries = _.cloneDeep(state.openDiscoveries);
          const activeDiscovery = openDiscoveries[action.discoveryId].present;

          let { pointData: focusedDataPoints } = action;
          const { focusedData = [] } = activeDiscovery;

          if (!_.isEmpty(focusedData)) {
            // remove data points not in focusedData
            focusedDataPoints = _.filter(
              focusedDataPoints,
              _focusedDataPoint => {
                return _.some(focusedData, _pathInfo =>
                  _.isEqual(_focusedDataPoint.pathInfo, _pathInfo),
                );
              },
            );
          }

          openDiscoveries[action.discoveryId].present = {
            ...activeDiscovery,
            collectDetailInfoArgs: _.isEmpty(focusedDataPoints)
              ? null
              : action.collectDetailInfoArgs,
            focusedDataPoints,
          };

          return {
            ...state,
            openDiscoveries,
          };
        }

        default:
          // @NOTE: this if statement has side effects with other reducers see DSC-3794
          // Also @NOTE, at this point, no reducers have been 'triggered'
          if (action.discoveryId) {
            const discoveries = state.openDiscoveries;
            if (
              action.reduxTransactionId &&
              action.reduxTransactionId ===
                discoveries[action.discoveryId].activeReduxTransactionId
            ) {
              enforcePresentState();
              // do not add to the current stack
              discoveries[action.discoveryId].present =
                OpenDiscoveryInternalReducer(
                  state.openDiscoveries[action.discoveryId].present,
                  action,
                );
            } else {
              const changedDiscovery = DiscoveryReducers[action.discoveryId](
                state.openDiscoveries[action.discoveryId],
                action,
              );

              // shallow equate
              if (changedDiscovery === discoveries[action.discoveryId]) {
                return state;
              }

              discoveries[action.discoveryId] = changedDiscovery;
            }

            // Notify ChartSpec of action. They have an opportunity to cancel the current action
            const chartType = _.get(
              discoveries[action.discoveryId].present,
              'viz.chartType',
            );
            if (chartType) {
              let { reduxTransactionId } = action;

              // Create a transaction id if not present so all action triggered from notifying ChartSpec are grouped
              if (!reduxTransactionId) {
                reduxTransactionId = shortid.generate();
                action = { ...action, reduxTransactionId };
              }

              // Put it back on the execution stack to avoid Redux errors from dispatching events in a reducer
              setTimeout(() =>
                ChartSpecs[chartType].onAction(
                  store.dispatch,
                  discoveries[action.discoveryId].present,
                  action,
                ),
              );
            }

            // Store current groupBy
            discoveries[action.discoveryId].activeReduxTransactionId =
              action.reduxTransactionId;

            return {
              ...state,
              openDiscoveries: { ...discoveries },
            };
          }
      }
    } catch (e) {
      console.warn(
        `Failed to execute ${action.type}. Returning current state.`,
        e,
      );
    }
    return state;
  },
  {
    monitors: [],
    fetching: false,
    fetched: false,
    error: null,
    deleting: false,
    deleted: false,
    showAddMonitorDialog: false,
    showSaveDialog: false,
    saveActive: false,
    openDiscoveries: {},
    openDiscoveriesTabList: [],
    displayDiscovery: null,
    queryResults: null,
    rangeSelection: '30d',
  },
  undefined,
);
